export type FilePreviewKind = 'image' | 'pdf' | 'unsupported';

/**
 * Decide how a file should be previewed based on its MIME type.
 * Images are rendered with a zoomable/pannable viewer, PDFs with the PDF
 * viewer, and everything else falls back to an "unsupported" placeholder.
 */
export const getFilePreviewKind = (
  mimeType?: string | null
): FilePreviewKind => {
  if (!mimeType) {
    return 'unsupported';
  }

  const normalized = mimeType.toLowerCase();

  if (normalized.includes('image')) {
    return 'image';
  }

  if (normalized.includes('pdf')) {
    return 'pdf';
  }

  return 'unsupported';
};

/**
 * Image MIME types that browsers cannot render in an <img> tag. Such files
 * fall back to a download-only placeholder instead of a broken image.
 */
const NON_RENDERABLE_IMAGE_TOKENS = ['tiff', 'heic', 'heif'];

/**
 * Whether an image MIME type can be displayed inline by the browser.
 * Returns false for non-image types and for image formats browsers don't
 * support natively (e.g. tiff, heic/heif).
 */
export const isRenderableImageMimeType = (
  mimeType?: string | null
): boolean => {
  if (getFilePreviewKind(mimeType) !== 'image') {
    return false;
  }

  const normalized = (mimeType ?? '').toLowerCase();

  return !NON_RENDERABLE_IMAGE_TOKENS.some((token) =>
    normalized.includes(token)
  );
};

/**
 * Client-side contract for HEIC→JPEG previews produced by the backend.
 *
 * The backend converts HEIC/HEIF uploads (typically iPhone photos, which browsers
 * cannot render) into a full-resolution JPEG served as `previewUrl`. A component
 * decides WHAT to render from `previewStatus`; `resolvePreviewSrc` only resolves the
 * `<img src>` for the terminal render branches.
 *
 * Values mirror the backend `PreviewStatus` enum. Kept local so these utils compile
 * regardless of whether the generated API types have been regenerated yet;
 * comparisons are by string value.
 */
export enum PreviewStatus {
  Pending = 'pending',
  Ready = 'ready',
  Failed = 'failed',
  NotApplicable = 'not_applicable',
}

/** Minimal shape needed to resolve a preview — a superset of every file DTO. */
export interface PreviewableFile {
  url?: string | null;
  previewUrl?: string | null;
  previewStatus?: PreviewStatus | string | null;
}

/**
 * Poll interval (ms) for re-fetching a query while a HEIC→JPEG preview is still
 * being generated, so a `pending → ready` transition reaches the UI without a
 * manual reload. Used as `refetchInterval` (gated on {@link isPreviewPending}) by
 * every surface that renders a previewable file.
 */
export const PREVIEW_POLL_INTERVAL_MS = 3000;

/**
 * Reconcile a file snapshotted in local state (e.g. a selected/active file frozen
 * in `useState`) against a freshly re-fetched copy: replace ONLY the preview fields
 * (+ refreshed signed `url`), keep every other local field, and return the SAME
 * reference when nothing changed so a poll-driven refetch never triggers a needless
 * re-render. Used by surfaces whose preview reads from a snapshot rather than live
 * query data.
 */
export const withFreshPreview = <T extends PreviewableFile>(
  local: T,
  fresh: T | undefined
): T => {
  if (
    !fresh ||
    (local.previewStatus === fresh.previewStatus &&
      local.previewUrl === fresh.previewUrl &&
      local.url === fresh.url)
  ) {
    return local;
  }
  return {
    ...local,
    previewStatus: fresh.previewStatus,
    previewUrl: fresh.previewUrl,
    url: fresh.url,
  };
};

const isStatus = (
  file: PreviewableFile | null | undefined,
  status: PreviewStatus
): boolean => file?.previewStatus === status;

export const isPreviewPending = (
  file: PreviewableFile | null | undefined
): boolean => isStatus(file, PreviewStatus.Pending);
export const isPreviewReady = (
  file: PreviewableFile | null | undefined
): boolean => isStatus(file, PreviewStatus.Ready);
export const isPreviewFailed = (
  file: PreviewableFile | null | undefined
): boolean => isStatus(file, PreviewStatus.Failed);

/**
 * True when the file is a HEIC awaiting/failing preview generation — i.e. it must
 * NOT be rendered via the original `url` (the browser can't display raw HEIC).
 * `pending` shows a spinner; `failed`/incomplete-`ready` shows an unavailable
 * placeholder.
 */
export const isPreviewBlocking = (
  file: PreviewableFile | null | undefined
): boolean =>
  isPreviewPending(file) ||
  isPreviewFailed(file) ||
  (isPreviewReady(file) && !file?.previewUrl);

/**
 * The `<img src>` to render:
 * - `ready` → the JPEG `previewUrl` (or undefined if missing → caller shows placeholder)
 * - `failed` / `pending` → undefined (never fall back to the raw HEIC `url`)
 * - `not_applicable` / no status → the original `url` (unchanged legacy behaviour)
 */
export function resolvePreviewSrc(
  file: PreviewableFile | null | undefined
): string | undefined {
  if (!file) {
    return undefined;
  }
  if (file.previewStatus === PreviewStatus.Ready) {
    return file.previewUrl ?? undefined;
  }
  if (
    file.previewStatus === PreviewStatus.Failed ||
    file.previewStatus === PreviewStatus.Pending
  ) {
    return undefined;
  }
  return file.url ?? undefined;
}
